// src/lib/demand-contract.ts
//
// DEM-2010 — Contrato de demanda (espelho frontend da FONTE ÚNICA do backend).
//
// O contrato canonical vive em `raios` (src/engine/demands/demand-contract.ts):
// `deriveDemandFields(input)` deriva os campos obrigatórios da demanda a partir
// do material informado. A UI NÃO consegue importar cross-repo, então este módulo
// é o espelho 1:1 da MESMA regra de derivação — mantido em sincronia com o backend.
//
// USO NA UI (AC#2, anti-fricção): a NewDemandDialog VALIDA os campos contra o
// contrato e DERIVA um PREVIEW dos campos faltantes via `deriveDemandFields`,
// SEM obrigar o usuário a preencher manualmente o que o backstop deriva. A
// submissão só é bloqueada em violações REAIS (ex: business_id ausente quando o
// pipeline exige) — o resto é derivado, igual ao backend.
//
// Paridade: a lógica abaixo reproduz exatamente a do backend (objective distinto,
// context, scope_in, scope_out, outcome_expected, delivery_artifact_expected).

export interface DemandDeriveInput {
  title?: string | null;
  /** Na UI, o campo "Descrição" do form. */
  description?: string | null;
  objective?: string | null;
  acceptanceCriteria?: string[] | null;
  creatorContext?: string | null;
  deliveryArtifactExpected?: string | null;
  context?: string | null;
  scopeIn?: string | null;
  scopeOut?: string | null;
  outcomeExpected?: string | null;
}

export interface DerivedDemandFields {
  objective: string;
  context: string;
  scopeIn: string;
  scopeOut: string;
  outcomeExpected: string;
  deliveryArtifactExpected: string;
}

function pick(v: string | null | undefined): string {
  return typeof v === 'string' ? v.trim() : '';
}

function normalizeForEquivalence(v: string): string {
  return v.toLowerCase().replace(/\s+/g, ' ').replace(/[.,;:!?]+$/g, '').trim();
}

/**
 * Espelho de `resolveDistinctObjective` (backend). Resolve um objective CURTO e
 * DISTINTO da description. Se colidir, reduz a 1ª frase a um resumo marcado.
 */
export function resolveDistinctObjective(
  explicitObjective: string,
  title: string,
  description: string,
): string {
  const firstSentence = description.split(/[.\n]/)[0]?.trim() ?? '';
  let candidate = (explicitObjective || title || firstSentence || description.slice(0, 120)).trim();
  candidate = candidate.slice(0, 500);

  const descNorm = normalizeForEquivalence(description);
  const candNorm = normalizeForEquivalence(candidate);
  const overlaps =
    candNorm.length > 0 &&
    (candNorm === descNorm ||
      (descNorm.includes(candNorm) && descNorm.length - candNorm.length <= 20) ||
      (candNorm.includes(descNorm) && candNorm.length - descNorm.length <= 20));
  if (overlaps) {
    const words = firstSentence.split(/\s+/).filter(Boolean).slice(0, 12).join(' ');
    candidate = `Entregar: ${words || (title || 'escopo definido')}`.slice(0, 200);
  }
  return candidate;
}

/**
 * DEM-2010 — espelho de `deriveDemandFields` (backend, fonte única). Deriva os
 * campos do contrato sempre não-vazios; prefere o valor informado e só deriva o
 * que falta. Determinística e idempotente.
 */
export function deriveDemandFields(input: DemandDeriveInput): DerivedDemandFields {
  const title = pick(input.title);
  const description = pick(input.description);
  const acceptance = (input.acceptanceCriteria ?? [])
    .map((c) => (typeof c === 'string' ? c.trim() : ''))
    .filter((c) => c.length > 0);
  const acceptanceJoined = acceptance.length > 0 ? acceptance.join('; ') : 'escopo cumprido';
  const creatorContext = pick(input.creatorContext);

  const deliveryArtifactExpected = (
    pick(input.deliveryArtifactExpected) ||
    `Artefato/entrega que comprove os acceptance criteria: ${acceptanceJoined}`
  ).slice(0, 1000);

  const objective = resolveDistinctObjective(pick(input.objective), title, description);

  const context =
    pick(input.context) ||
    creatorContext ||
    `Contexto da demanda: ${description || title || acceptanceJoined}`.slice(0, 2000);
  const scopeIn = pick(input.scopeIn) || `Entra no escopo: ${acceptanceJoined}`.slice(0, 2000);
  const scopeOut =
    pick(input.scopeOut) ||
    'Fora do escopo: qualquer mudança não coberta pelos acceptance_criteria acima.';
  const outcomeExpected =
    pick(input.outcomeExpected) || `Resultado esperado: ${deliveryArtifactExpected}`.slice(0, 2000);

  return {
    objective,
    context,
    scopeIn,
    scopeOut,
    outcomeExpected,
    deliveryArtifactExpected,
  };
}

/** Campo do contrato (chave UI + rótulo humano) pro preview de derivação. */
export interface DerivedFieldPreview {
  key: keyof DerivedDemandFields;
  label: string;
  value: string;
}

const FIELD_LABELS: Record<keyof DerivedDemandFields, string> = {
  objective: 'Objetivo',
  context: 'Contexto',
  scopeIn: 'Dentro do escopo',
  scopeOut: 'Fora do escopo',
  outcomeExpected: 'Resultado esperado',
  deliveryArtifactExpected: 'Artefato de entrega',
};

/**
 * AC#2 — calcula o PREVIEW dos campos do contrato que o usuário NÃO preencheu e
 * que serão DERIVADOS automaticamente (na origem + backstop). Só lista os que
 * estavam vazios — não força preenchimento manual. Retorna [] quando o usuário
 * preencheu tudo.
 */
export function previewDerivedFields(input: DemandDeriveInput): DerivedFieldPreview[] {
  const derived = deriveDemandFields(input);
  const emptyMap: Record<keyof DerivedDemandFields, boolean> = {
    objective: pick(input.objective).length === 0,
    context: pick(input.context).length === 0,
    scopeIn: pick(input.scopeIn).length === 0,
    scopeOut: pick(input.scopeOut).length === 0,
    outcomeExpected: pick(input.outcomeExpected).length === 0,
    deliveryArtifactExpected: pick(input.deliveryArtifactExpected).length === 0,
  };
  const out: DerivedFieldPreview[] = [];
  (Object.keys(emptyMap) as Array<keyof DerivedDemandFields>).forEach((k) => {
    if (emptyMap[k]) {
      out.push({ key: k, label: FIELD_LABELS[k], value: derived[k] });
    }
  });
  return out;
}
